import React, { useEffect, useRef } from "react";
import { useThemeStore } from "~/store/themeStore";
import { acquirePopoutDocument } from "~/utils/popoutWindowsRegistry";

// The <popout-theme-probe> custom element (defined in popoutWindowsRegistry)
// fires adopted/connectedCallback when the panel moves between documents.

// Mirror whatever the main window considers canonical (body class + PrimeReact
// theme-link href, which themeStore mutates in place — invisible to the lib's
// childList-only head observer). No-op when doc IS the main document.
function syncThemeInto(doc) {
  if (!doc || doc === document || !doc.body) return;
  doc.body.classList.toggle(
    "dark-mode-custom",
    document.body.classList.contains("dark-mode-custom"),
  );
  const mainLink = document.getElementById("theme-link");
  const popoutLink = doc.getElementById("theme-link");
  if (mainLink && popoutLink && popoutLink.href !== mainLink.href) {
    popoutLink.href = mainLink.href;
  }
}

// Mounted inside every docked panel via the DockModeLayout factory (PoC #popout).
// Two roles when the panel lands in a popout document: sync the theme into it
// and register it in popoutWindowsRegistry so global listeners can reach it.
export default function PopoutPanelBridge() {
  const ref = useRef(null);
  const releaseRef = useRef(null);
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el._onDocumentChange = () => {
      const doc = el.ownerDocument;
      releaseRef.current?.();
      releaseRef.current = doc !== document ? acquirePopoutDocument(doc) : null;
      syncThemeInto(doc);
    };
    // The probe may already sit in a popout document by the time this effect
    // runs (first mount happens after insertion) — sync the initial state.
    el._onDocumentChange();
    return () => {
      el._onDocumentChange = null;
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, []);

  useEffect(() => {
    syncThemeInto(ref.current?.ownerDocument);
  }, [theme]);

  return (
    <popout-theme-probe
      ref={ref}
      data-popout-theme-bridge=""
      style={{ display: "none" }}
    />
  );
}
